import type { ApiEnvelope } from '@/api/errors';
import type { ProfileType, UserProfile, UserRole } from '@/types/auth';
import type { CompanyCard, CompanySearchItem } from '@/types/buy';
import { BaseService } from './BaseService';

const PROFILE_TYPES = new Set<ProfileType>(['INDIVIDUAL', 'SELF_EMPLOYED', 'LEGAL_ENTITY']);

function normalizeProfileType(value: string | undefined): ProfileType | undefined {
  return value && PROFILE_TYPES.has(value as ProfileType) ? (value as ProfileType) : undefined;
}

interface MainInfoDto {
  readonly id?: number;
  readonly email?: string;
  readonly firstName?: string;
  readonly lastName?: string;
  readonly patronymicName?: string;
  readonly name?: string;
  readonly inn?: string;
  readonly phone?: string;
  readonly contact?: string;
  readonly roles?: UserRole[];
  readonly type?: string;
  readonly isVerified?: boolean;
}

// Сервис user-service: профиль/персональные данные (personal/*).
class UserService extends BaseService {
  /** Профиль пользователя (для шапки и /app/profile). GET — идемпотентно. */
  async getProfile(): Promise<UserProfile> {
    const response = await this.request<ApiEnvelope<MainInfoDto>>(
      { method: 'get', url: '/personal/main' },
      { idempotent: true },
    );
    const data = this.unwrap(response.data, response.status);
    return {
      id: data.id ?? 0,
      email: data.email ?? '',
      firstName: data.firstName,
      lastName: data.lastName,
      patronymicName: data.patronymicName,
      name: data.name,
      inn: data.inn,
      phone: data.phone,
      contact: data.contact,
      roles: data.roles ?? [],
      type: normalizeProfileType(data.type),
      isVerified: data.isVerified ?? false,
    };
  }

  /**
   * Обновление контактной информации. Шлём МИНИМАЛЬНЫЙ payload `{ userId, mainInfo: { contact } }`
   * (как старый портал) — бэкенд мёржит, данные компании/реквизитов не затираются. Мутация — без повтора.
   */
  async updateContact(userId: number, contact: string): Promise<void> {
    await this.request(
      { method: 'post', url: '/personal/summary', data: { userId, mainInfo: { contact } } },
      { retry: false },
    );
  }

  // ── Поиск контрагентов (грузополучатель и т.п.) ───────────────────────────────
  /** Поиск компаний по названию/ИНН/ОГРН (бэкенд обогащает данными из ДаДата). */
  async searchCompanies(filter: string, signal?: AbortSignal): Promise<CompanySearchItem[]> {
    const response = await this.request<ApiEnvelope<{ items?: CompanySearchItem[] }>>(
      { method: 'get', url: '/user/search/companyInfo', params: { filter, limit: 7 } },
      { idempotent: true, signal },
    );
    return response.data?.data?.items ?? [];
  }

  /** Карточка компании по ИНН; 204/пусто — компания не найдена → null. */
  async getCompanyByInn(inn: string, signal?: AbortSignal): Promise<CompanyCard | null> {
    const response = await this.request<ApiEnvelope<CompanyCard>>(
      { method: 'get', url: '/user/companyInfo', params: { inn } },
      { idempotent: true, signal },
    );
    if (response.status === 204 || !response.data?.data) {
      return null;
    }
    return response.data.data;
  }
}

export const userService = new UserService();
